import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Bot, Sparkles, Code2, Globe, Zap, MessageSquare, Languages, BookOpen,
  UserPlus, Check, Send, Loader2, ArrowRight, ShieldCheck,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";
import { resolveBot, publicBotChat, createCheckout } from "@/lib/api";
import { toast } from "sonner";

const FEATURES = [
  { icon: Sparkles, title: "Auto-build from your URL", body: "Paste your website and AI writes the bot's name, greeting, starter questions and behavior instantly." },
  { icon: BookOpen, title: "Trained on your content", body: "Point it at your pages — it learns your facts and answers with cited sources, not guesses." },
  { icon: Globe, title: "Live web research", body: "When something isn't in your knowledge base, it fetches current info from the web and links its sources." },
  { icon: Code2, title: "One-line embed + REST API", body: "Drop a single <script> tag on any site, or call the API to build your own UI. Each bot has its own key." },
  { icon: MessageSquare, title: "Remembers the conversation", body: "Real multi-turn memory keeps context across the whole chat, so replies stay relevant." },
  { icon: Languages, title: "Speaks your visitor's language", body: "Detects the visitor's language automatically and replies in it — no setup needed." },
  { icon: UserPlus, title: "Captures leads for you", body: "Spots emails and phone numbers in the chat and saves them as leads in your dashboard." },
  { icon: ShieldCheck, title: "Multi-client & isolated", body: "Run unlimited separate bots — perfect for agencies managing many client websites." },
];

const STEPS = [
  { n: "01", title: "Create", body: "Paste your website URL — AI auto-builds your bot in seconds." },
  { n: "02", title: "Train", body: "Add pages so it answers in your voice, with your facts." },
  { n: "03", title: "Embed", body: "Copy one line of code onto your site. You're live." },
];

const PLANS = [
  { key: null, name: "Free", price: "$0", cadence: "forever", tagline: "Try it on one site",
    features: ["1 chatbot", "100 messages / mo", "Embed widget + REST API", "Train on 1 website"], cta: "Start free" },
  { key: "chatbot_pro_monthly", name: "Pro", price: "$29", cadence: "/month", tagline: "For growing businesses", popular: true,
    features: ["5 chatbots", "3,000 messages / mo", "Live web research", "Lead capture", "Remove WebsiteTech badge"], cta: "Choose Pro" },
  { key: "chatbot_business_monthly", name: "Business", price: "$99", cadence: "/month", tagline: "For agencies & teams",
    features: ["Unlimited chatbots", "25,000 messages / mo", "Multi-client management", "Priority support", "All features"], cta: "Choose Business" },
];

export default function AiChatbot() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loadingKey, setLoadingKey] = useState(null);

  useEffect(() => { document.title = "AI Chatbot for Any Website · WebsiteTech.pro"; }, []);

  const choosePlan = async (plan) => {
    if (!plan.key) { navigate("/login"); return; }
    if (!user) { toast.info("Create a free account first, then upgrade in a click."); navigate("/login"); return; }
    setLoadingKey(plan.key);
    try {
      const { checkout_url } = await createCheckout(plan.key);
      window.location.href = checkout_url;
    } catch {
      toast.error("Could not start checkout. Please try again.");
      setLoadingKey(null);
    }
  };

  return (
    <>
      <Navbar />
      <main data-testid="ai-chatbot-page">
        {/* Hero */}
        <section className="bg-gradient-to-br from-[#0B203E] to-[#071428] text-white pt-32 pb-20 md:pt-40 md:pb-28 relative overflow-hidden">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-white/10 border border-white/15 rounded-full px-3 py-1.5 mb-6 backdrop-blur-sm">
                <Bot size={15} className="text-blue-300" />
                <span className="text-xs font-semibold uppercase tracking-wider text-blue-200">AI Chatbot Platform</span>
              </div>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05]">
                An AI assistant for your website, <span className="text-blue-300">live in 60 seconds</span>
              </h1>
              <p className="text-blue-100/80 text-base md:text-lg mt-6 max-w-xl">
                Build a branded chatbot trained on your own site, then embed it anywhere with one line of code. No developers. No code. It answers questions, speaks your visitor's language, and captures leads while you sleep.
              </p>
              <div className="flex flex-wrap gap-4 mt-8">
                <button onClick={() => navigate("/login")} data-testid="hero-start-free"
                  className="bg-white text-[#0B203E] font-semibold rounded-xl px-6 py-3.5 shadow-lg hover:-translate-y-0.5 transition-transform inline-flex items-center gap-2">
                  Start free <ArrowRight size={17} />
                </button>
                <a href="#try-it" data-testid="hero-try-it"
                  className="bg-white/10 text-white border border-white/20 rounded-xl px-6 py-3.5 backdrop-blur-sm hover:bg-white/20 transition-colors inline-flex items-center gap-2">
                  <Zap size={16} className="text-blue-300" /> Try the live demo
                </a>
              </div>
              <p className="text-blue-100/50 text-xs mt-5">No credit card required · Free plan forever</p>
            </div>
            <div id="try-it" className="scroll-mt-28">
              <DemoBot />
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="py-20 md:py-28 bg-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-2xl">
              <span className="overline text-brand">Everything included</span>
              <h2 className="font-heading text-3xl md:text-4xl font-extrabold text-slate-900 mt-2">Not just a chat box — a real assistant</h2>
              <p className="text-slate-500 mt-3 md:text-lg">Grounded in your content, honest about what it doesn't know, and built to turn conversations into customers.</p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-12">
              {FEATURES.map((f, i) => (
                <motion.div key={i} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: (i % 4) * 0.05 }}
                  className="bg-slate-50 border border-slate-100 rounded-2xl p-6 hover:border-brand/30 hover:shadow-lg transition-all">
                  <div className="w-11 h-11 rounded-xl bg-blue-50 flex items-center justify-center mb-4"><f.icon size={20} className="text-brand" /></div>
                  <h3 className="font-heading text-base font-bold text-slate-900">{f.title}</h3>
                  <p className="text-sm text-slate-500 mt-1.5 leading-relaxed">{f.body}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* How it works */}
        <section className="py-20 md:py-24 bg-slate-50">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="font-heading text-3xl md:text-4xl font-extrabold text-slate-900 text-center">Live in three steps</h2>
            <div className="grid md:grid-cols-3 gap-6 mt-12">
              {STEPS.map((s) => (
                <div key={s.n} className="bg-white border border-slate-200 rounded-2xl p-8 card-shadow">
                  <span className="font-mono text-3xl font-extrabold text-blue-200">{s.n}</span>
                  <h3 className="font-heading text-xl font-bold text-slate-900 mt-3">{s.title}</h3>
                  <p className="text-slate-500 mt-2">{s.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Pricing */}
        <section className="py-20 md:py-28 bg-white" id="pricing">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-2xl mx-auto">
              <span className="overline text-brand">Pricing</span>
              <h2 className="font-heading text-3xl md:text-4xl font-extrabold text-slate-900 mt-2">Simple plans that scale with you</h2>
              <p className="text-slate-500 mt-3">Start free. Upgrade when you're ready. Cancel anytime.</p>
            </div>
            <div className="grid md:grid-cols-3 gap-6 mt-14 items-stretch">
              {PLANS.map((p) => (
                <div key={p.name} data-testid={`plan-${p.name.toLowerCase()}`}
                  className={`relative rounded-3xl p-8 flex flex-col ${p.popular ? "bg-gradient-to-br from-[#0B203E] to-[#071428] text-white shadow-2xl md:-translate-y-3" : "bg-white border border-slate-200 card-shadow"}`}>
                  {p.popular && <span className="absolute top-5 right-5 bg-blue-500 text-white text-[10px] font-bold uppercase tracking-wider rounded-full px-3 py-1">Most popular</span>}
                  <h3 className={`font-heading text-lg font-bold ${p.popular ? "text-white" : "text-slate-900"}`}>{p.name}</h3>
                  <p className={`text-sm ${p.popular ? "text-blue-100/70" : "text-slate-400"}`}>{p.tagline}</p>
                  <div className="mt-5 flex items-end gap-1">
                    <span className={`font-heading text-4xl font-extrabold ${p.popular ? "text-white" : "text-slate-900"}`}>{p.price}</span>
                    <span className={`text-sm mb-1 ${p.popular ? "text-blue-100/60" : "text-slate-400"}`}>{p.cadence}</span>
                  </div>
                  <ul className="mt-6 space-y-3 flex-1">
                    {p.features.map((f, i) => (
                      <li key={i} className={`flex items-start gap-2.5 text-sm ${p.popular ? "text-blue-50" : "text-slate-600"}`}>
                        <Check size={17} className={p.popular ? "text-blue-300 mt-0.5 shrink-0" : "text-brand mt-0.5 shrink-0"} /> {f}
                      </li>
                    ))}
                  </ul>
                  <button onClick={() => choosePlan(p)} disabled={!!p.key && loadingKey === p.key} data-testid={`plan-cta-${p.name.toLowerCase()}`}
                    className={`mt-8 w-full rounded-xl px-5 py-3.5 font-semibold inline-flex items-center justify-center gap-2 transition-transform hover:-translate-y-0.5 disabled:opacity-60 ${p.popular ? "bg-white text-[#0B203E]" : "bg-brand text-white"}`}>
                    {p.key && loadingKey === p.key ? <Loader2 size={17} className="animate-spin" /> : p.cta}
                  </button>
                </div>
              ))}
            </div>
            <p className="text-center text-xs text-slate-400 mt-8">Secure checkout by Stripe · Test mode. Use card 4242 4242 4242 4242 with any future expiry and CVC.</p>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-20 bg-slate-50">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h2 className="font-heading text-3xl md:text-4xl font-extrabold text-slate-900">Ready to put AI on your website?</h2>
            <p className="text-slate-500 mt-3 md:text-lg">Build your first assistant free — no card, no code.</p>
            <button onClick={() => navigate("/login")} data-testid="footer-start-free"
              className="mt-8 bg-brand text-white font-semibold rounded-xl px-7 py-4 inline-flex items-center gap-2 hover:-translate-y-0.5 transition-transform">
              Start building free <ArrowRight size={18} />
            </button>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}

/* Inline live demo of a real bot (uses the WebsiteTech assistant). */
function DemoBot() {
  const [cfg, setCfg] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const sessionRef = useRef("demo_" + Math.random().toString(36).slice(2));
  const bodyRef = useRef(null);

  useEffect(() => { resolveBot("websitetech").then(setCfg).catch(() => setCfg(null)); }, []);
  useEffect(() => { if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight; }, [msgs, busy]);

  const send = async (text) => {
    const t = (text || "").trim();
    if (!t || busy || !cfg) return;
    setInput("");
    setMsgs((m) => [...m, { role: "u", content: t }]);
    setBusy(true);
    try {
      const d = await publicBotChat(cfg.public_key, { session_id: sessionRef.current, message: t });
      setMsgs((m) => [...m, { role: "a", content: d.answer || "Sorry, I couldn't respond.", sources: d.sources || [] }]);
    } catch {
      setMsgs((m) => [...m, { role: "a", content: "Sorry, I'm having trouble connecting." }]);
    }
    setBusy(false);
  };

  return (
    <div className="bg-white rounded-2xl shadow-2xl overflow-hidden max-w-md mx-auto lg:ml-auto" data-testid="demo-bot">
      <div className="bg-[#0B203E] px-5 py-4 flex items-center gap-3 border-b border-white/10">
        <div className="w-9 h-9 rounded-lg bg-blue-500 flex items-center justify-center"><Bot size={18} className="text-white" /></div>
        <div><p className="text-white font-bold text-sm leading-tight">{cfg?.name || "WebsiteTech Assistant"}</p><p className="text-blue-200/70 text-[11px]">Live demo · try it now</p></div>
      </div>
      <div ref={bodyRef} className="h-72 overflow-y-auto p-4 bg-slate-50 space-y-3">
        {msgs.length === 0 && (
          <>
            <div className="bg-white border border-slate-200 rounded-2xl rounded-bl-sm px-3.5 py-2.5 text-sm text-slate-700 max-w-[85%]">
              {cfg?.welcome_message || "Hi! Ask me anything about building an AI website."}
            </div>
            <div className="flex flex-wrap gap-2 pt-1">
              {(cfg?.suggestions || ["What can you build?", "How much does it cost?", "Do you do SEO?"]).slice(0, 3).map((s, i) => (
                <button key={i} onClick={() => send(s)} data-testid="demo-suggestion"
                  className="bg-white border border-slate-300 text-slate-600 rounded-full px-3 py-1.5 text-xs hover:bg-slate-100 transition-colors">{s}</button>
              ))}
            </div>
          </>
        )}
        {msgs.map((m, i) => (
          <div key={i} className={m.role === "u" ? "flex justify-end" : "flex"}>
            <div className={`max-w-[85%] px-3.5 py-2.5 rounded-2xl text-sm whitespace-pre-wrap ${m.role === "u" ? "bg-brand text-white rounded-br-sm" : "bg-white border border-slate-200 text-slate-700 rounded-bl-sm"}`}>
              {m.content}
              {(m.sources || []).length > 0 && (
                <div className="mt-2 space-y-0.5">
                  {m.sources.slice(0, 3).map((u, j) => (
                    <a key={j} href={u} target="_blank" rel="noopener noreferrer" className="block text-[11px] text-slate-400 hover:text-brand truncate">↗ {u.replace(/^https?:\/\//, "")}</a>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
        {busy && <div className="flex"><div className="bg-white border border-slate-200 rounded-2xl px-4 py-3"><Loader2 size={15} className="animate-spin text-slate-400" /></div></div>}
      </div>
      <div className="p-3 border-t border-slate-100 flex gap-2 bg-white">
        <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send(input)}
          placeholder="Type a message…" data-testid="demo-input"
          className="flex-1 text-sm bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-slate-900 outline-none focus:border-brand" />
        <button onClick={() => send(input)} disabled={busy} data-testid="demo-send"
          className="bg-brand text-white rounded-lg w-11 flex items-center justify-center disabled:opacity-50"><Send size={16} /></button>
      </div>
    </div>
  );
}
